// src/context/brandVoice/fieldValue.js — "is this the same value?" for the five widgets.
//
// ⛔ Decision 1 (2026-09-27): a field is CHANGED only when its value differs from the
// loaded one — never because it was "touched". The PUT stamps everything it receives as
// the tenant's, so a field that merely re-rendered (`null` → `""`, a missing claims
// category → `[]`) must compare EQUAL, or it would lock the AI out of it for nothing.
// Strings compare trimmed and whitespace-collapsed: the server collapses whitespace on
// write, so "a  b" and "a b" are the same stored text.
import { CLAIM_CATEGORIES } from '../brandIdentityApi.js';
import { toWire } from './wireShape.js';

const text = (v) => (typeof v === 'string' ? v.replace(/\s+/g, ' ').trim() : v == null ? '' : String(v));
const record = (r) => {
  if (!r || typeof r !== 'object') return {};
  return Object.fromEntries(
    Object.keys(r).sort()
      .map((k) => [k, text(r[k])])
      .filter(([, v]) => v !== ''),
  );
};
const list = (v) => (Array.isArray(v) ? v : []);

export function canonical(widget, value) {
  switch (widget) {
    case 'scalar': return text(value);
    // ⭐ v186: blank entries are dropped by `toWire`, so an empty added row is not a change.
    case 'str_list': return toWire({ widget }, list(value)).map(text);
    // ⭐ The draft holds ROWS, the read holds strings: both are compared as the strings
    // the PUT would send (a fully blank row is dropped there, so it is not a change).
    case 'pair_list': return toWire({ widget }, list(value)).map(text);
    // ⭐ v187: blank rows are dropped and empty values stripped by `toWire`.
    case 'record_list': return toWire({ widget }, list(value)).map(record);
    case 'claims': {
      // ⭐ v188: blank rows dropped / empty values stripped per category by `toWire`.
      const src = toWire({ widget }, value && typeof value === 'object' && !Array.isArray(value) ? value : {});
      return Object.fromEntries(CLAIM_CATEGORIES.map((c) => [c, list(src[c]).map(record)]));
    }
    default: return value ?? null;
  }
}

export function sameValue(field, a, b) {
  return JSON.stringify(canonical(field.widget, a)) === JSON.stringify(canonical(field.widget, b));
}

/** The voice read → the voice draft shape the PUT takes. */
export function voiceDraftFromRead(voice) {
  if (!voice) return null;
  return {
    label: voice.label ?? '',
    axes: Object.fromEntries((voice.axes || []).map((a) => [a.axis, a.guidance ?? ''])),
  };
}

export function sameVoice(a, b) {
  if (!a || !b) return a === b;
  const keys = [...new Set([...Object.keys(a.axes || {}), ...Object.keys(b.axes || {})])].sort();
  return text(a.label) === text(b.label)
    && keys.every((k) => text(a.axes?.[k]) === text(b.axes?.[k]));
}
